import { useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Upload } from 'lucide-react';
import { saasApi } from '@/services/api';
import { resolveFileUrl } from '@/utils/fileUrl';

async function uploadImage(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await saasApi.uploadFile(fd);
  return res.data.message.file_url as string;
}

/** Upload button + URL input for CMS / SEO images. */
export default function ImageUrlField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  hint?: string;
}) {
  const [busy, setBusy] = useState(false);

  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="mt-2 flex flex-wrap items-start gap-3">
        <div className="w-28 h-20 rounded-xl border border-dashed border-gray-300 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            <img src={resolveFileUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImagePlus className="w-6 h-6 text-gray-300" />
          )}
        </div>
        <div className="flex-1 min-w-[200px] space-y-2">
          <input
            className="input-field text-sm"
            placeholder="Image URL or /files/…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <label className={`btn-secondary text-xs inline-flex items-center gap-1.5 cursor-pointer ${busy ? 'opacity-60' : ''}`}>
              <Upload className="w-3.5 h-3.5" />
              {busy ? 'Uploading…' : 'Upload image'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={busy}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  setBusy(true);
                  try {
                    const url = await uploadImage(file);
                    onChange(url);
                    toast.success('Image uploaded');
                  } catch {
                    toast.error('Upload failed');
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            </label>
            {value && (
              <button type="button" className="text-xs text-red-600" onClick={() => onChange('')}>
                Clear
              </button>
            )}
          </div>
          {hint && <p className="text-xs text-gray-400">{hint}</p>}
        </div>
      </div>
    </div>
  );
}
